import { useRef, useEffect, useCallback } from "react";
import { CATEGORY_COLORS } from "./CategoryChips";

interface GraphNode {
  id: number;
  category: string;
  title: string;
  importance: number;
  createdAt: string | null;
  subcategory: string | null;
  sourceType: string | null;
  brandId?: number | null;
  adAccountId?: number | null;
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
  fx?: number | null;
  fy?: number | null;
  index?: number;
}

interface GraphEdge {
  source: number | GraphNode;
  target: number | GraphNode;
  relationshipType: string;
  strength: number;
}

interface SimNode extends GraphNode {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

interface KnowledgeGraphProps {
  nodes: GraphNode[];
  edges: GraphEdge[];
  selectedNodeId: number | null;
  onSelectNode: (id: number | null) => void;
  searchTerm: string;
  brandColorMap?: Map<number, string>;
}

export function KnowledgeGraph({ nodes, edges, selectedNodeId, onSelectNode, searchTerm, brandColorMap }: KnowledgeGraphProps) {
  const brandColorMapRef = useRef<Map<number, string>>(brandColorMap ?? new Map());
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const simNodesRef = useRef<SimNode[]>([]);
  const simEdgesRef = useRef<GraphEdge[]>([]);
  const simulationRef = useRef<any>(null);
  const transformRef = useRef({ x: 0, y: 0, scale: 1 });
  const hoveredNodeRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const lastTransformRef = useRef({ x: 0, y: 0 });
  const animFrameRef = useRef<number>(0);
  const selectedRef = useRef<number | null>(null);
  const searchRef = useRef("");
  const settledRef = useRef(false);

  selectedRef.current = selectedNodeId;
  searchRef.current = searchTerm;
  brandColorMapRef.current = brandColorMap ?? new Map();

  const getConnectedIds = useCallback((nodeId: number | null): Set<number> => {
    if (!nodeId) return new Set();
    const ids = new Set<number>();
    for (const e of simEdgesRef.current) {
      const srcId = typeof e.source === "object" ? (e.source as GraphNode).id : e.source;
      const tgtId = typeof e.target === "object" ? (e.target as GraphNode).id : e.target;
      if (srcId === nodeId) ids.add(tgtId);
      if (tgtId === nodeId) ids.add(srcId);
    }
    return ids;
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const w = canvas.width;
    const h = canvas.height;
    const { x: tx, y: ty, scale } = transformRef.current;
    const simNodes = simNodesRef.current;
    const simEdges = simEdgesRef.current;
    const selId = selectedRef.current;
    const hovId = hoveredNodeRef.current;
    const sLower = searchRef.current.toLowerCase();

    const connected = getConnectedIds(selId);

    ctx.clearRect(0, 0, w, h);

    ctx.save();
    ctx.scale(2, 2);
    ctx.translate(tx, ty);
    ctx.scale(scale, scale);

    for (const e of simEdges) {
      const s = typeof e.source === "object" ? e.source as SimNode : null;
      const t = typeof e.target === "object" ? e.target as SimNode : null;
      if (!s || !t || s.x == null || t.x == null) continue;

      let alpha = 0.15;
      if (selId) {
        if (s.id === selId || t.id === selId) alpha = 0.6;
        else alpha = 0.05;
      }

      ctx.beginPath();
      ctx.moveTo(s.x, s.y);
      ctx.lineTo(t.x, t.y);
      ctx.strokeStyle = `rgba(209,205,198,${alpha})`;
      ctx.lineWidth = Math.max(0.5, e.strength * 2);
      ctx.stroke();
    }

    for (const n of simNodes) {
      const color = CATEGORY_COLORS[n.category] || "#6B7280";
      const radius = 4 + (n.importance / 10) * 8;
      const isSelected = n.id === selId;
      const isConnected = connected.has(n.id);
      const isHovered = n.id === hovId;
      const brandColor = (n.brandId != null) ? brandColorMapRef.current.get(n.brandId) : undefined;
      const hasBrandRing = !!brandColor && brandColorMapRef.current.size > 1;

      let nodeAlpha = 1;
      if (selId && !isSelected && !isConnected) nodeAlpha = 0.2;
      if (sLower && !n.title.toLowerCase().includes(sLower)) nodeAlpha = 0.15;

      ctx.globalAlpha = nodeAlpha;

      if (isSelected || isHovered) {
        ctx.beginPath();
        ctx.arc(n.x, n.y, radius + 4, 0, Math.PI * 2);
        ctx.fillStyle = `${color}30`;
        ctx.fill();
      }

      // Brand ring indicator: outer ring in brand color
      if (hasBrandRing) {
        ctx.beginPath();
        ctx.arc(n.x, n.y, radius + 2.5, 0, Math.PI * 2);
        ctx.strokeStyle = brandColor!;
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      ctx.beginPath();
      ctx.arc(n.x, n.y, radius, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();

      if (isSelected) {
        ctx.strokeStyle = "white";
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      if ((isHovered || isSelected || isConnected) && nodeAlpha > 0.5) {
        ctx.fillStyle = "#111827";
        ctx.font = "600 11px Inter, sans-serif";
        ctx.textAlign = "center";
        const label = n.title.length > 30 ? n.title.slice(0, 28) + "..." : n.title;
        ctx.fillText(label, n.x, n.y - radius - 6);
      }

      ctx.globalAlpha = 1;
    }

    ctx.restore();
  }, [getConnectedIds]);

  const scheduleRedraw = useCallback(() => {
    cancelAnimationFrame(animFrameRef.current);
    animFrameRef.current = requestAnimationFrame(draw);
  }, [draw]);

  useEffect(() => {
    scheduleRedraw();
  }, [selectedNodeId, searchTerm, scheduleRedraw]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const w = container.clientWidth;
    const h = container.clientHeight;
    canvas.width = w * 2;
    canvas.height = h * 2;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;

    const simNodes: SimNode[] = nodes.map((n) => {
      const existing = simNodesRef.current.find(sn => sn.id === n.id);
      return {
        ...n,
        x: existing?.x ?? w / 2 + (Math.random() - 0.5) * w * 0.6,
        y: existing?.y ?? h / 2 + (Math.random() - 0.5) * h * 0.6,
        vx: 0,
        vy: 0,
      };
    });

    const simEdges: GraphEdge[] = edges.map(e => ({ ...e }));

    simNodesRef.current = simNodes;
    simEdgesRef.current = simEdges;
    settledRef.current = false;

    if (simulationRef.current) {
      simulationRef.current.stop();
    }

    const d3 = (window as any).d3;
    if (d3 && d3.forceSimulation) {
      const simulation = d3.forceSimulation(simNodes)
        .force("charge", d3.forceManyBody().strength(-120))
        .force("link", d3.forceLink(simEdges).id((d: SimNode) => d.id).distance(100).strength((e: GraphEdge) => e.strength * 0.3))
        .force("x", d3.forceX(w / 2).strength(0.05))
        .force("y", d3.forceY(h / 2).strength(0.05))
        .force("collide", d3.forceCollide().radius((d: SimNode) => 8 + (d.importance / 10) * 10))
        .alphaDecay(0.03)
        .on("tick", () => {
          scheduleRedraw();
        })
        .on("end", () => {
          settledRef.current = true;
        });

      simulationRef.current = simulation;
    }

    draw();

    return () => {
      if (simulationRef.current) {
        simulationRef.current.stop();
      }
    };
  }, [nodes, edges]);

  const screenToWorld = useCallback((sx: number, sy: number): { x: number; y: number } => {
    const { x: tx, y: ty, scale } = transformRef.current;
    return { x: (sx - tx) / scale, y: (sy - ty) / scale };
  }, []);

  const findNodeAt = useCallback((sx: number, sy: number): SimNode | null => {
    const { x: wx, y: wy } = screenToWorld(sx, sy);
    const simNodes = simNodesRef.current;
    for (let i = simNodes.length - 1; i >= 0; i--) {
      const n = simNodes[i];
      const radius = 4 + (n.importance / 10) * 8;
      const dx = n.x - wx, dy = n.y - wy;
      if (dx * dx + dy * dy <= (radius + 4) * (radius + 4)) return n;
    }
    return null;
  }, [screenToWorld]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isDraggingRef.current = true;
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    lastTransformRef.current = { x: transformRef.current.x, y: transformRef.current.y };
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const sx = e.clientX - rect.left, sy = e.clientY - rect.top;

    if (isDraggingRef.current) {
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      transformRef.current.x = lastTransformRef.current.x + dx;
      transformRef.current.y = lastTransformRef.current.y + dy;
      scheduleRedraw();
      return;
    }

    const node = findNodeAt(sx, sy);
    const newId = node?.id ?? null;
    if (hoveredNodeRef.current !== newId) {
      hoveredNodeRef.current = newId;
      scheduleRedraw();
    }
    if (canvasRef.current) {
      canvasRef.current.style.cursor = node ? "pointer" : "grab";
    }
  }, [findNodeAt, scheduleRedraw]);

  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    const wasDragging = isDraggingRef.current;
    isDraggingRef.current = false;
    if (!wasDragging) return;

    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    if (Math.abs(dx) < 3 && Math.abs(dy) < 3) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
      const node = findNodeAt(sx, sy);
      onSelectNode(node?.id ?? null);
    }
  }, [findNodeAt, onSelectNode]);

  const handleDoubleClick = useCallback(() => {
    const simNodes = simNodesRef.current;
    if (simNodes.length === 0) return;
    const container = containerRef.current;
    if (!container) return;

    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const n of simNodes) {
      minX = Math.min(minX, n.x);
      maxX = Math.max(maxX, n.x);
      minY = Math.min(minY, n.y);
      maxY = Math.max(maxY, n.y);
    }

    const w = container.clientWidth, h = container.clientHeight;
    const padding = 60;
    const graphW = (maxX - minX) || 1, graphH = (maxY - minY) || 1;
    const scale = Math.min((w - padding * 2) / graphW, (h - padding * 2) / graphH, 2);
    const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;

    transformRef.current = {
      x: w / 2 - cx * scale,
      y: h / 2 - cy * scale,
      scale,
    };
    scheduleRedraw();
  }, [scheduleRedraw]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const wheelHandler = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const rect = canvas.getBoundingClientRect();
      const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const newScale = Math.min(3, Math.max(0.2, transformRef.current.scale * delta));
      const ratio = newScale / transformRef.current.scale;
      transformRef.current.x = sx - (sx - transformRef.current.x) * ratio;
      transformRef.current.y = sy - (sy - transformRef.current.y) * ratio;
      transformRef.current.scale = newScale;
      scheduleRedraw();
    };

    canvas.addEventListener("wheel", wheelHandler, { passive: false });
    return () => canvas.removeEventListener("wheel", wheelHandler);
  }, [scheduleRedraw]);

  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;
      const w = container.clientWidth, h = container.clientHeight;
      canvas.width = w * 2;
      canvas.height = h * 2;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      draw();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [draw]);

  return (
    <div ref={containerRef} className="w-full h-full relative" data-testid="knowledge-graph">
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => { isDraggingRef.current = false; hoveredNodeRef.current = null; scheduleRedraw(); }}
        onDoubleClick={handleDoubleClick}
        className="w-full h-full"
        style={{ cursor: "grab", touchAction: "none" }}
      />
      {nodes.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="text-sm text-gray-400">No nodes to display</p>
        </div>
      )}
    </div>
  );
}
